import { TOKEN_WAIT_MS, stubTurnstileToken, type TurnstileApi } from './turnstile';

/**
 * Turnstile for the guarded API calls (w5-11, decision 94; the contract's `x-turnstile`): the
 * boarding-pass scan, the first flight search of a check, uploads and every link email. The
 * HTTP client asks `turnstileBroker.token()` for a fresh single-use token and sends it as
 * `X-Turnstile-Token` (lib/api/http.ts). S7 keeps its own widget (`useTurnstile`).
 *
 * One invisible widget for the whole funnel, rendered by `<TurnstileHost />` (chrome/) in the shell: it
 * works out a token as soon as the page loads, so the first guarded call rarely waits, and a
 * new one after each is taken. Cloudflare shows it only when it wants an interaction; the
 * host then shows it in a small panel at the bottom with one line of copy.
 *
 * Without `VITE_TURNSTILE_SITE_KEY` (tests, the fake API, a local stack): no widget, no
 * script, the stub token (the API's fake verifier accepts it).
 */
export interface TurnstileSource {
  /** A fresh token: waits for the widget up to TOKEN_WAIT_MS, else '' (the API then refuses). */
  token(): Promise<string>;
}

interface Widget {
  api: TurnstileApi;
  id: string;
}

class Broker implements TurnstileSource {
  siteKey: string | undefined = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  private widget: Widget | null = null;
  private ready: string | null = null;
  private waiters: ((t: string) => void)[] = [];
  /** The script never loaded (offline, a blocker): don't wait for a token that won't come. */
  private unavailable = false;

  token(): Promise<string> {
    if (!this.siteKey) return Promise.resolve(stubTurnstileToken());
    if (this.ready) {
      const t = this.ready;
      this.ready = null;
      if (this.widget) this.widget.api.reset(this.widget.id); // work out the next one now
      return Promise.resolve(t);
    }
    if (this.unavailable) return Promise.resolve('');
    return new Promise<string>((resolve) => {
      const done = (t: string) => {
        clearTimeout(timer);
        resolve(t);
      };
      const timer = setTimeout(() => {
        this.waiters = this.waiters.filter((w) => w !== done);
        resolve('');
      }, TOKEN_WAIT_MS);
      this.waiters.push(done);
    });
  }

  /* The host's side. */
  attach(widget: Widget) {
    this.widget = widget;
    this.unavailable = false;
  }
  detach(id: string) {
    if (this.widget?.id !== id) return;
    this.widget = null;
    this.ready = null;
  }
  issued(t: string) {
    const waiter = this.waiters.shift();
    if (!waiter) {
      this.ready = t;
      return;
    }
    waiter(t); // taken at once: work out the next one for whoever comes after
    if (this.widget) this.widget.api.reset(this.widget.id);
  }
  expired() {
    this.ready = null;
  }
  failedToLoad() {
    this.unavailable = true;
    this.waiters.splice(0).forEach((w) => w(''));
  }

  /** Tests: back to a fresh page. */
  resetForTests(siteKey?: string) {
    this.siteKey = siteKey;
    this.widget = null;
    this.ready = null;
    this.waiters = [];
    this.unavailable = false;
  }
}

export const turnstileBroker = new Broker();
